import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Box, Button, Chip, Grid, IconButton, Stack } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { PageContainer } from "@/features/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/features/authentication";
import { chartOfAccountTypeLabels } from "@/features/chart-of-accounts";
import { UserOutletsSelect } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, formatNumber } from "@/features/shared";

export const OutletChartOfAccounts = () => {
  const { changeOutlet, hasPermission, outlet } = useAuth();
  const navigate = useNavigate();

  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Function to fetch outlet chart of accounts
  const fetchOutletChartOfAccounts = useCallback(async () => {
    if (!outlet) return;

    try {
      setLoading(true);

      const {
        data: { data: chartOfAccounts },
      } = await apiOutletClient.get("chart-of-accounts", {
        headers: {
          "Outlet-Id": outlet.id,
        },
        params: {
          withTotalBalance: true,
        },
      });

      // Filter out virtual root (parent_id is null)
      const filteredAccounts = chartOfAccounts.filter(
        (account) => account.parentId !== null,
      );

      setChartOfAccounts(filteredAccounts);
    } catch (error) {
      console.error("Failed to fetch outlet chart of accounts:", error);
      setChartOfAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [outlet]);

  useEffect(() => {
    fetchOutletChartOfAccounts();
  }, [fetchOutletChartOfAccounts]);

  // Build tree paths from nested set model (lft/rgt)
  const chartOfAccountsWithPaths = useMemo(() => {
    if (!chartOfAccounts.length) return [];

    // Sort by lft to ensure proper order
    const sortedRows = [...chartOfAccounts].sort((a, b) => a.lft - b.lft);

    // Build paths for each row based on nested set model
    return sortedRows.map((row) => {
      const path = [];

      // Find all ancestors (nodes that contain this node)
      sortedRows.forEach((potentialAncestor) => {
        if (
          potentialAncestor.lft < row.lft &&
          potentialAncestor.rgt > row.rgt
        ) {
          path.push(potentialAncestor.code);
        }
      });

      // Add current node to path
      path.push(row.code);

      return {
        ...row,
        path: path,
      };
    });
  }, [chartOfAccounts]);

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_OUTLET_CHART_OF_ACCOUNTS,
  ) && (
    <Button fullWidth variant="contained" onClick={() => navigate("create")}>
      Tambah
    </Button>
  );

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 2,
      renderCell: ({ row, value }) => (
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <span>{value}</span>
          {row.isOutletSpecific && (
            <Chip label="Outlet" color="info" size="small" />
          )}
        </Stack>
      ),
    },
    {
      field: "type",
      headerName: "Tipe Akun",
      flex: 2,
      renderCell: ({ value }) => chartOfAccountTypeLabels[value],
    },
    {
      field: "totalBalance",
      headerName: "Saldo",
      flex: 2,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "deactivatedAt",
      headerName: "Status",
      flex: 1,
      renderCell: ({ value }) => {
        const isDeactivated = Boolean(value);
        return (
          <Chip
            label={isDeactivated ? "Nonaktif" : "Aktif"}
            color={isDeactivated ? "default" : "success"}
          />
        );
      },
    },
  ];

  // Add action column for view and adding child accounts
  columns.push({
    field: "actions",
    type: "actions",
    headerName: "Aksi",
    width: 120,
    getActions: (params) => {
      const actions = [
        <IconButton
          key="view"
          onClick={() => navigate(`${params.row.id}`)}
          size="small"
        >
          <VisibilityIcon />
        </IconButton>,
      ];

      if (hasPermission(PermissionEnum.CREATE_OUTLET_CHART_OF_ACCOUNTS)) {
        actions.push(
          <IconButton
            key="add"
            onClick={() => {
              navigate("create", {
                state: {
                  parentChartOfAccount: params.row,
                  code: `${params.row.code}.`,
                  type: params.row.type,
                },
              });
            }}
            size="small"
          >
            <AddIcon />
          </IconButton>,
        );
      }

      return actions;
    },
  });

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Grid container spacing={2} sx={{ justifyContent: "flex-end" }}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <UserOutletsSelect
                value={outlet}
                onChange={changeOutlet}
                TextFieldProps={{
                  label: "Pilih Outlet",
                  required: true,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <DataGridPro
          rows={chartOfAccountsWithPaths}
          loading={loading}
          autoHeight
          treeData
          getTreeDataPath={(row) => row.path}
          defaultGroupingExpansionDepth={-1}
          groupingColDef={{
            headerName: "Kode",
            flex: 3,
          }}
          columns={columns}
          hideFooterRowCount
          showToolbar
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
